/**
 * Cleanroom TypeScript implementation of the mathematical cinematography and
 * quality-control primitives from `Sac-Y/match-cut` (`scripts/render.py` and
 * `scripts/qc.py`, MIT License, Copyright (c) 2026 Sac-Y, inspected at commit
 * b51c98f5c795b046ceb1c69396368dbabd88d5d9).
 *
 * Covers:
 *   - Hermite smoothstep easing (`ease`)
 *   - Template-A constant-cover camera roll zoom (`templateACoverZoom`, `rollAngleDeg`)
 *   - Anchor-matching affine warp (`computeRelayAffine`: fit + geo + pull + roll)
 *   - Camera whip-cut profiles (`whipProfileAt`: `whip_out` / `whip_in`)
 *   - Soft-knee highlight compression & film toe/halation math (`softKnee`, `applyFilmToe`)
 *   - Cut-pair QC metrics (`evaluateCutPair`: centre drift %, scale delta %, BT.709 luma step %)
 */

export type Point2D = readonly [number, number];

export interface GeoTransform {
    /** Source anchor in canvas pixels */
    src: Point2D;
    /** Destination anchor in canvas pixels */
    dst: Point2D;
    /** Scale multiplier applied around the anchor */
    scale: number;
}

export interface PullTransform {
    cSrc: Point2D;
    cDst: Point2D;
    kEnd: number;
    i0: number;
    maskRadii?: Point2D;
}

export type WhipTuple =
    | readonly [number, number, number]
    | readonly [number, number, number, number, number];

export interface WhipState {
    scale: number;
    dy: number;
    blurY: number;
    dx: number;
    blurX: number;
}

export interface RelayShotSpec {
    id: string;
    name: string;
    world: string;
    template: 'A-overhead' | 'B-tabletop' | 'C-canted';
    startFrame: number;
    endFrame: number;
    /** Subject anchor in normalized [0..1, 0..1] canvas coordinates before geo correction */
    rawAnchor: Point2D;
    /** Subject radius as a fraction of canvas height before geo correction */
    rawScale: number;
    /** Mean BT.709 luminance in [0..1] */
    meanLuma: number;
    /** Action angle in degrees (carried across the cut) */
    actionAngleDeg: number;
    roll?: boolean;
    rollSeg?: readonly [number, number];
    stepOk?: boolean;
    geo?: GeoTransform;
    pull?: PullTransform;
    whipOut?: readonly WhipTuple[];
    whipIn?: readonly WhipTuple[];
    accent: string;
    groundTint: string;
}

export interface CutPairQC {
    fromId: string;
    toId: string;
    cutFrame: number;
    /** Euclidean anchor distance as a percentage of canvas width */
    centreDriftPct: number;
    /** Relative subject size difference as a percentage */
    scaleDriftPct: number;
    /** Relative BT.709 brightness step: (Lb - La) / max(La, Lb) */
    lumaStepPct: number;
    /** Action angle difference in degrees */
    angleDeltaDeg: number;
    /** True when centreDriftPct <= 3% and scaleDriftPct <= 5% */
    geometryAligned: boolean;
    /** True when |lumaStepPct| > 10% and incoming shot is not marked stepOk */
    lumaFlagged: boolean;
}

/** Hermite smoothstep: t^2 * (3 - 2t) clamped to [0, 1]. */
export function hermiteEase(t: number): number {
    const c = Math.max(0, Math.min(1, t));
    return c * c * (3 - 2 * c);
}

/**
 * Computes the constant zoom factor required so a frame of aspect ratio `width / height`
 * rotated by up to `maxAngleRad` never exposes black corners (`ZOOM_A` in `render.py`).
 */
export function templateACoverZoom(
    dps = 20,
    maxFrames = 20,
    fps = 25,
    aspect = 16 / 9,
): { maxAngleRad: number; coverZoom: number } {
    const maxAngleRad = ((dps * Math.max(0, maxFrames - 1)) / 2 / Math.max(1, fps)) * (Math.PI / 180);
    const coverZoom = Math.cos(maxAngleRad) + aspect * Math.sin(maxAngleRad);
    return { maxAngleRad, coverZoom };
}

/**
 * Clockwise camera roll angle in degrees for frame `frameInShot` of a shot of length `shotFrames`.
 */
export function rollAngleDeg(
    frameInShot: number,
    shotFrames: number,
    globalFrame: number,
    options: {
        dps?: number;
        fps?: number;
        maxFrames?: number;
        rollSeg?: readonly [number, number];
    } = {},
): number {
    const dps = options.dps ?? 20;
    const fps = options.fps ?? 25;
    const maxFrames = options.maxFrames ?? 20;
    const { maxAngleRad } = templateACoverZoom(dps, maxFrames, fps);
    const thMaxDeg = (maxAngleRad * 180) / Math.PI;

    if (options.rollSeg) {
        const [s0, ns] = options.rollSeg;
        return thMaxDeg * ((2 * (globalFrame - s0)) / Math.max(1, ns - 1) - 1);
    }
    if (shotFrames > maxFrames) {
        return thMaxDeg * ((2 * frameInShot) / Math.max(1, shotFrames - 1) - 1);
    }
    return (dps * (frameInShot - (shotFrames - 1) / 2)) / Math.max(1, fps);
}

/**
 * Evaluates the camera whip state (`whip_out` on outgoing tail frames, `whip_in`
 * on incoming head frames).
 */
export function whipProfileAt(
    frameInShot: number,
    shotFrames: number,
    whipOut?: readonly WhipTuple[],
    whipIn?: readonly WhipTuple[],
): WhipState {
    let entry: WhipTuple | undefined;
    if (whipOut && whipOut.length > 0) {
        const idx = frameInShot - (shotFrames - whipOut.length);
        if (idx >= 0 && idx < whipOut.length) entry = whipOut[idx];
    }
    if (!entry && whipIn && frameInShot >= 0 && frameInShot < whipIn.length) {
        entry = whipIn[frameInShot];
    }
    if (!entry) {
        return { scale: 1, dy: 0, blurY: 0, dx: 0, blurX: 0 };
    }
    return {
        scale: entry[0],
        dy: entry[1],
        blurY: entry[2],
        dx: entry.length === 5 ? entry[3] : 0,
        blurX: entry.length === 5 ? entry[4] : 0,
    };
}

/**
 * Soft-knee highlight compression from `render.py`:
 * values above `k` roll off smoothly via `k + (1 - k) * tanh((x - k) / (1 - k))`.
 */
export function softKnee(x: number, k = 0.8): number {
    const clamped = Math.max(0, x);
    if (clamped <= k || k >= 1) return Math.min(1, clamped);
    return k + (1 - k) * Math.tanh((clamped - k) / (1 - k));
}

/**
 * Lifted shadow toe curve from `render.py`: `x + toe * clamp(1 - x / 0.22, 0, 1)^2`.
 */
export function applyFilmToe(x: number, toe = 0.022): number {
    const c = Math.max(0, Math.min(1, x));
    const shadow = Math.max(0, Math.min(1, 1 - c / 0.22));
    return Math.min(1, c + toe * shadow * shadow);
}

/**
 * Computes the effective subject anchor and scale after applying `geo` and `pull`
 * at normalized shot progress `u` in [0..1].
 */
export function effectiveSubjectPose(
    shot: RelayShotSpec,
    canvasWidth: number,
    canvasHeight: number,
    frameInShot: number,
): { cx: number; cy: number; scale: number } {
    const shotFrames = Math.max(1, shot.endFrame - shot.startFrame);
    let cx = shot.rawAnchor[0] * canvasWidth;
    let cy = shot.rawAnchor[1] * canvasHeight;
    let scale = shot.rawScale;

    if (shot.geo) {
        const { src, dst, scale: s } = shot.geo;
        cx = s * cx + (dst[0] - s * src[0]);
        cy = s * cy + (dst[1] - s * src[1]);
        scale *= s;
    }

    if (shot.pull && frameInShot >= shot.pull.i0) {
        const denom = Math.max(1, shotFrames - 1 - shot.pull.i0);
        const e = hermiteEase((frameInShot - shot.pull.i0) / denom);
        const kk = 1 + (shot.pull.kEnd - 1) * e;
        const cdx = shot.pull.cSrc[0] + (shot.pull.cDst[0] - shot.pull.cSrc[0]) * e;
        const cdy = shot.pull.cSrc[1] + (shot.pull.cDst[1] - shot.pull.cSrc[1]) * e;
        cx = kk * (cx - shot.pull.cSrc[0]) + cdx;
        cy = kk * (cy - shot.pull.cSrc[1]) + cdy;
        scale *= kk;
    }

    return { cx, cy, scale };
}

/**
 * Evaluates a cut pair (`outgoing` last frame -> `incoming` first frame) using
 * the exact tolerances from `references/post.md` and `scripts/qc.py`:
 *   - Centre alignment within 3% of canvas width
 *   - Subject size within 5%
 *   - Brightness step under 10% (unless `incoming.stepOk` is true)
 */
export function evaluateCutPair(
    outgoing: RelayShotSpec,
    incoming: RelayShotSpec,
    canvasWidth = 1920,
    canvasHeight = 1080,
): CutPairQC {
    const outFrames = Math.max(1, outgoing.endFrame - outgoing.startFrame);
    const poseOut = effectiveSubjectPose(outgoing, canvasWidth, canvasHeight, outFrames - 1);
    const poseIn = effectiveSubjectPose(incoming, canvasWidth, canvasHeight, 0);

    const distPx = Math.hypot(poseIn.cx - poseOut.cx, poseIn.cy - poseOut.cy);
    const centreDriftPct = Number(((distPx / Math.max(1, canvasWidth)) * 100).toFixed(2));

    const maxScale = Math.max(poseOut.scale, poseIn.scale, 1e-6);
    const scaleDriftPct = Number(((Math.abs(poseIn.scale - poseOut.scale) / maxScale) * 100).toFixed(2));

    const maxLuma = Math.max(outgoing.meanLuma, incoming.meanLuma, 1e-6);
    const lumaStepPct = Number((((incoming.meanLuma - outgoing.meanLuma) / maxLuma) * 100).toFixed(1));

    const angleDeltaDeg = Number(Math.abs(incoming.actionAngleDeg - outgoing.actionAngleDeg).toFixed(1));

    const geometryAligned = centreDriftPct <= 3.0 && scaleDriftPct <= 5.0;
    const lumaFlagged = Math.abs(lumaStepPct) > 10.0 && !incoming.stepOk;

    return {
        fromId: outgoing.id,
        toId: incoming.id,
        cutFrame: incoming.startFrame,
        centreDriftPct,
        scaleDriftPct,
        lumaStepPct,
        angleDeltaDeg,
        geometryAligned,
        lumaFlagged,
    };
}
